import React, { useEffect, useState } from "react";
import { fuerzasApi, apiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Shield, Upload, Trash2, Plus, Clapperboard, ListChecks, Globe2, Radio, Rocket } from "lucide-react";

export default function FuerzasAdmin() {
  const [prog, setProg] = useState("coach");
  const [tab, setTab] = useState("videos");
  const [meta, setMeta] = useState({ programas: [] });
  useEffect(() => { fuerzasApi.meta().then(setMeta).catch(() => {}); }, []);
  const TABS = [
    { k: "videos", label: "Videos Express", icon: Clapperboard },
    { k: "acciones", label: "Acciones Diarias", icon: ListChecks },
    { k: "influencia", label: "Influencia", icon: Globe2 },
    { k: "lives", label: "Lives", icon: Radio },
    { k: "expansion", label: "Expansión", icon: Rocket },
  ];
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="fuerzas-admin">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-xl bg-navy grid place-items-center"><Shield className="w-6 h-6 text-[var(--gold)]" /></div>
        <div><h1 className="text-2xl font-extrabold text-[#0A1C30]">Fuerzas Especiales</h1><p className="text-sm text-[#6B7688]">Administra Coach Junior y Head Coach Junior Supera.</p></div>
      </div>
      <div className="flex gap-2 mb-5">
        {(meta.programas || []).map((p) => (
          <button key={p.id} data-testid={`fz-prog-${p.id}`} onClick={() => setProg(p.id)} className={`px-4 py-2 rounded-xl text-sm font-extrabold ${prog === p.id ? "bg-[var(--gold)] text-[#0A1C30]" : "bg-white ring-1 ring-black/10 text-[#6B7688]"}`}>{p.name}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => <button key={t.k} data-testid={`fz-tab-${t.k}`} onClick={() => setTab(t.k)} className={`px-4 py-2 rounded-xl text-sm font-bold ${tab === t.k ? "bg-navy text-white" : "bg-white ring-1 ring-black/10 text-[#6B7688]"}`}>{t.label}</button>)}
      </div>
      {tab === "videos" && <VideosAdmin prog={prog} />}
      {tab === "acciones" && <AccionesAdmin prog={prog} />}
      {tab === "influencia" && <InfluenciaAdmin prog={prog} />}
      {tab === "lives" && <LivesAdmin prog={prog} />}
      {tab === "expansion" && <ExpansionAdmin prog={prog} />}
    </div>
  );
}

function VideosAdmin({ prog }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ titulo: "", descripcion: "", link: "" });
  const [file, setFile] = useState(null);
  const load = () => fuerzasApi.videos(prog).then(setRows).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [prog]);
  const add = async () => {
    const fd = new FormData(); fd.append("titulo", f.titulo); fd.append("descripcion", f.descripcion); fd.append("link", f.link);
    if (file) fd.append("file", file);
    if (!f.titulo.trim() && !f.link.trim() && !file) { toast.error("Agrega título y un video (archivo o enlace)."); return; }
    try { await fuerzasApi.addVideo(prog, fd); setF({ titulo: "", descripcion: "", link: "" }); setFile(null); toast.success("Video agregado."); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="bg-white rounded-2xl ring-1 ring-black/5 p-5" data-testid="fz-videos">
      <div className="space-y-2 rounded-xl ring-1 ring-black/5 p-3 mb-4">
        <Input data-testid="fz-video-titulo" placeholder="Título del video" value={f.titulo} onChange={(e) => setF((s) => ({ ...s, titulo: e.target.value }))} />
        <Textarea placeholder="Descripción" rows={2} value={f.descripcion} onChange={(e) => setF((s) => ({ ...s, descripcion: e.target.value }))} />
        <Input placeholder="Enlace (YouTube/Drive) — o sube archivo" value={f.link} onChange={(e) => setF((s) => ({ ...s, link: e.target.value }))} />
        <label className="flex items-center gap-2 text-sm text-[#6B7688] cursor-pointer"><Upload className="w-4 h-4" /> {file ? file.name : "Subir video (MP4/WebM/MOV, máx 200MB)"}<input type="file" accept="video/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} /></label>
        <button data-testid="fz-video-add" onClick={add} className="rounded-lg bg-[var(--gold)] text-[#0A1C30] font-bold px-4 py-2 text-sm">Agregar video</button>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-xs text-[#9AA3B2]">Sin videos.</p>}
        {rows.map((v) => (
          <div key={v.id} className="flex items-center justify-between rounded-lg bg-[#F4F6F9] p-3"><div><p className="font-bold text-sm text-[#0A1C30]">{v.titulo}</p><p className="text-xs text-[#6B7688]">{v.descripcion}{v.tiene_archivo ? " · 🎬 archivo" : v.link ? " · enlace" : ""}</p></div><button onClick={async () => { await fuerzasApi.delVideo(v.id); load(); }} className="text-[#C0392B] p-1"><Trash2 className="w-4 h-4" /></button></div>
        ))}
      </div>
    </div>
  );
}

function UserPicker({ prog, value, onChange }) {
  const [users, setUsers] = useState([]);
  useEffect(() => { fuerzasApi.users(prog).then(setUsers).catch(() => {}); }, [prog]);
  return (
    <select data-testid="fz-user-select" className="w-full h-10 rounded-md border border-black/10 px-2 text-sm bg-white mb-3" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Selecciona un usuario…</option>
      {users.map((u) => <option key={u.id} value={u.id}>{u.nombre} · {u.email}{u.colegio ? ` · ${u.colegio}` : ""}</option>)}
    </select>
  );
}

function AccionesAdmin({ prog }) {
  const [uid, setUid] = useState("");
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ fecha: "", titulo: "", descripcion: "" });
  const load = () => { if (uid) fuerzasApi.userAcciones(uid).then(setRows).catch(() => {}); else setRows([]); };
  useEffect(() => { load(); }, [uid]);
  const add = async () => { if (!f.titulo.trim()) { toast.error("Escribe la actividad."); return; } try { await fuerzasApi.addUserAccion(uid, f); setF({ fecha: "", titulo: "", descripcion: "" }); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="bg-white rounded-2xl ring-1 ring-black/5 p-5" data-testid="fz-acciones">
      <UserPicker prog={prog} value={uid} onChange={setUid} />
      {uid && (
        <>
          <div className="space-y-2 rounded-xl ring-1 ring-black/5 p-3 mb-4">
            <div className="grid grid-cols-2 gap-2"><Input type="date" value={f.fecha} onChange={(e) => setF((s) => ({ ...s, fecha: e.target.value }))} /><Input data-testid="fz-accion-titulo" placeholder="Actividad" value={f.titulo} onChange={(e) => setF((s) => ({ ...s, titulo: e.target.value }))} /></div>
            <Textarea placeholder="Descripción" rows={2} value={f.descripcion} onChange={(e) => setF((s) => ({ ...s, descripcion: e.target.value }))} />
            <button data-testid="fz-accion-add" onClick={add} className="rounded-lg bg-[var(--gold)] text-[#0A1C30] font-bold px-4 py-2 text-sm">Asignar actividad</button>
          </div>
          <div className="space-y-2">
            {rows.map((a) => <div key={a.id} className="flex items-center justify-between rounded-lg bg-[#F4F6F9] p-3"><div><p className="font-bold text-sm text-[#0A1C30]">{a.titulo} {a.completada ? "✅" : ""}</p><p className="text-xs text-[#6B7688]">{a.fecha} {a.descripcion}</p></div><button onClick={async () => { await fuerzasApi.delUserAccion(a.id); load(); }} className="text-[#C0392B] p-1"><Trash2 className="w-4 h-4" /></button></div>)}
          </div>
        </>
      )}
    </div>
  );
}

function InfluenciaAdmin({ prog }) {
  const [uid, setUid] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => { if (uid) fuerzasApi.userInfluencia(uid).then(setData).catch(() => {}); else setData(null); }, [uid]);
  return (
    <div className="bg-white rounded-2xl ring-1 ring-black/5 p-5" data-testid="fz-influencia">
      <UserPicker prog={prog} value={uid} onChange={setUid} />
      {data && (
        <div>
          <p className="text-sm text-[#0A1C30] mb-1"><b>Plan:</b> {data.plan || "—"}</p>
          <p className="text-sm font-bold text-[#177A45] mb-2">Personas influidas: {data.total_personas}</p>
          <div className="space-y-2">{data.acciones.map((a) => <div key={a.id} className="rounded-lg bg-[#F4F6F9] p-3"><p className="font-bold text-sm text-[#0A1C30]">{a.accion}</p><p className="text-xs text-[#6B7688]">{a.resultado} · {a.personas} personas</p></div>)}</div>
        </div>
      )}
    </div>
  );
}

function LivesAdmin({ prog }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { fuerzasApi.lives(prog).then(setRows).catch(() => {}); }, [prog]);
  return (
    <div className="bg-white rounded-2xl ring-1 ring-black/5 p-5" data-testid="fz-lives">
      <p className="font-extrabold text-[#0A1C30] mb-3">Agenda de Lives ({rows.length})</p>
      {rows.length === 0 && <p className="text-xs text-[#9AA3B2]">Sin reservaciones.</p>}
      <div className="space-y-2">{rows.map((l) => <div key={l.id} className="rounded-lg bg-[#F4F6F9] p-3"><p className="font-bold text-sm text-[#0A1C30]">{l.nombre} {l.apellido}</p><p className="text-xs text-[#6B7688]">{l.fecha ? new Date(l.fecha).toLocaleString() : ""} · WhatsApp {l.whatsapp}</p></div>)}</div>
    </div>
  );
}

function ExpansionAdmin({ prog }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { fuerzasApi.expansion(prog).then(setRows).catch(() => {}); }, [prog]);
  return (
    <div className="bg-white rounded-2xl ring-1 ring-black/5 p-5" data-testid="fz-expansion">
      <p className="font-extrabold text-[#0A1C30] mb-3">Contactos de Expansión ({rows.length})</p>
      {rows.length === 0 && <p className="text-xs text-[#9AA3B2]">Sin contactos.</p>}
      <div className="space-y-2">{rows.map((x) => <div key={x.id} className="rounded-lg bg-[#F4F6F9] p-3"><p className="font-bold text-sm text-[#0A1C30]">{x.institucion || x.contacto_nombre} <span className="text-xs text-[#6B7688]">({x.tipo})</span></p><p className="text-xs text-[#6B7688]">Enviado por {x.user_nombre} · {x.contacto_nombre}{x.cargo ? ` · ${x.cargo}` : ""}{x.telefono ? ` · ${x.telefono}` : ""}{x.email ? ` · ${x.email}` : ""}</p>{x.notas && <p className="text-xs text-[#3B4658] mt-1">{x.notas}</p>}</div>)}</div>
    </div>
  );
}
